import { memo, useMemo } from 'react';
import { frontRoyalStoreApi } from 'FrontRoyalStore';
import { useStatusBoxConfig } from './useStatusBoxConfig';
import { useStatusBoxDisplay } from './useStatusBoxDisplay';
import { StatusBoxPresentation } from './StatusBoxPresentation';
import { StatusBoxMobilePresentation } from './StatusBoxMobilePresentation';

function StatusBoxComponent({ mobile }: { mobile?: boolean }) {
    const { statusConfig, allPastDueCourses, allPastDueProjects } = useStatusBoxConfig();
    const { data: publishedStreamIdsWithAllContentStored = {} } =
        frontRoyalStoreApi.usePublishedStreamIdsWithAllContentStoredQuery();

    const {
        showAllCourses,
        showAllProjects,
        displayedCourses,
        displayedProjects,
        hasMoreCourses,
        hasMoreProjects,
        toggleShowAllCourses,
        toggleShowAllProjects,
        handleCoursePress,
    } = useStatusBoxDisplay(allPastDueCourses, allPastDueProjects);

    const presentationProps = useMemo(() => {
        // Return null if statusConfig is null to maintain type safety
        if (!statusConfig) {
            return null;
        }
        return {
            statusConfig,
            displayedCourses,
            displayedProjects,
            hasMoreCourses,
            hasMoreProjects,
            showAllCourses,
            showAllProjects,
            onToggleShowAllCourses: toggleShowAllCourses,
            onToggleShowAllProjects: toggleShowAllProjects,
            onCoursePress: handleCoursePress,
            totalCoursesCount: allPastDueCourses.length,
            totalProjectsCount: allPastDueProjects.length,
            publishedStreamIdsWithAllContentStored,
        };
    }, [
        statusConfig,
        displayedCourses,
        displayedProjects,
        hasMoreCourses,
        hasMoreProjects,
        showAllCourses,
        showAllProjects,
        toggleShowAllCourses,
        toggleShowAllProjects,
        handleCoursePress,
        allPastDueCourses.length,
        allPastDueProjects.length,
        publishedStreamIdsWithAllContentStored,
    ]);

    // No status config or presentation props
    if (!statusConfig || !presentationProps) {
        return null;
    }

    // Render appropriate component
    return mobile ? (
        <StatusBoxMobilePresentation {...presentationProps} />
    ) : (
        <StatusBoxPresentation {...presentationProps} />
    );
}

export const StatusBox = memo(StatusBoxComponent);
